import Star from '@/components/atom/icon/Star';
import { BookingAvailability } from '@/types/bff/gql-schema';
import { NonF2fDoctorVo } from '@/types/search';

interface Props {
  doctor: NonF2fDoctorVo;
  onClick: () => void;
}

const DoctorBookingStatusTime = ({ doctor }: Pick<Props, 'doctor'>) => {
  const { bookingAvailability, todaySchedule, tomorrowSchedule, isBookable } = doctor.bookingInfo;

  if (!isBookable) {
    return null;
  }

  switch (bookingAvailability) {
    case BookingAvailability.TodayBookable:
      return (
        <div className='flex flex-row items-center'>
          <div className='typography-ui-13sb text-green-700'>오늘 예약</div>
          <div className='mx-[6px] h-[10px] w-[1px] bg-gray-100'></div>
          <div className='typography-ui-13r text-gray-300'>{todaySchedule}</div>
        </div>
      );
    case BookingAvailability.TomorrowBookable:
      return (
        <div className='flex flex-row items-center'>
          <div className='typography-ui-13sb text-primary-600'>내일 예약</div>
          <div className='mx-[6px] h-[10px] w-[1px] bg-gray-100'></div>
          <div className='typography-ui-13r text-gray-300'>{tomorrowSchedule}</div>
        </div>
      );
    case BookingAvailability.NotBookable:
    default:
      return null;
  }
};

const formatReviewCount = (count: number) => {
  if (count >= 2000) return '2000+';
  if (count >= 1500) return '1500+';
  if (count >= 1000) return '1000+';
  if (count >= 500) return '500+';
  if (count >= 300) return '300+';
  if (count >= 200) return '200+';
  if (count >= 100) return '100+';
  if (count >= 50) return '50+';
  if (count >= 20) return '20+';
  if (count >= 10) return '10+';
  return String(count);
};

const Doctor = ({ doctor, onClick }: Props) => {
  const isSpecialist = !!doctor.professionalDepartment;
  const isReadyForVideoRequest = doctor.readyForVideoCall;
  const hasTag = isSpecialist || isReadyForVideoRequest;
  const reviewAverage = Number(doctor?.reviewAverageRating)?.toFixed(1);
  const hasProfileUrl = !!doctor.profileUrl;
  const isNotAvailable =
    doctor.bookingInfo.bookingAvailability === BookingAvailability.NotBookable || !doctor.bookingInfo.isBookable;

  return (
    <div className='flex items-center gap-[16px] self-stretch px-[24px] py-[20px]' onClick={onClick}>
      <div className='flex flex-1 flex-col items-start gap-[8px]'>
        <div className='flex flex-col items-start gap-[4px] self-stretch'>
          <div className='flex items-center gap-[4px] self-stretch'>
            <div className='typography-ui-17sb flex shrink-0 items-center text-gray-600'>{doctor.name}</div>
            <div className='typography-ui-13r flex-1 truncate text-gray-200'>{doctor.hospitalV2.hospitalName}</div>
          </div>
          <div className='flex items-center gap-[4px] self-stretch'>
            <Star />
            <div className='flex items-center'>
              <div className='typography-ui-13sb text-gray-600'>{reviewAverage}</div>
              <div className='typography-ui-13sb text-gray-300'>({formatReviewCount(doctor.reviewCount)})</div>
            </div>
          </div>
        </div>
        <DoctorBookingStatusTime doctor={doctor} />
        {hasTag && (
          <div className='flex flex-row gap-[6px]'>
            {isSpecialist && (
              <div className='typography-ui-12sb flex h-[20px] items-center justify-center gap-[16px] rounded-[4px] bg-blue-100 px-[4px] text-blue-600'>
                {doctor.professionalDepartment?.name}전문의
              </div>
            )}
            {isReadyForVideoRequest && (
              <div className='typography-ui-12sb flex h-[20px] items-center justify-center gap-[16px] rounded-[4px] bg-gray-30 px-[4px] text-gray-300'>
                영상진료
              </div>
            )}
          </div>
        )}
      </div>
      <div className='relative flex h-[72px] w-[72px] items-center justify-center overflow-hidden rounded-[36px]'>
        {hasProfileUrl ? (
          <div
            className='h-full w-full bg-center bg-cover'
            style={{ backgroundImage: `url("${doctor.profileUrl}")` }}
          />
        ) : (
          <div className='typography-ui-22sb flex h-full w-full items-center justify-center text-white'>
            {doctor.name}
          </div>
        )}
        {isNotAvailable && (
          <div className='typography-ui-13sb absolute flex h-full w-full items-center justify-center text-white'>
            예약 마감
          </div>
        )}
      </div>
    </div>
  );
};

export default Doctor;
